import classNames from "classnames";
import { useState } from "react";
import { Button } from "../../../components/buttons/button";
import { EmptyState } from "../../../components/empty";
import { EventImage } from "../../../components/event/eventImage";
import { Icon } from "../../../components/icon/icon";
import { MiniHeader } from "../../../components/typography/miniHeader";
import { RenderMarkdown } from "../../../components/typography/renderMarkdown";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import {
  CreatedEvent,
  CreatedTicket,
} from "../../../redux/features/event/event.types";
import { formatDate } from "../../../utils/helpers";
import { TicketPurchase } from "../../../utils/types";
import { ChooseTickets } from "./chooseTickets";
import { PreviewOrganizerDetails } from "./organizerDetails";

export const PreviewStep = ({
  event,
  tickets,
  ticketsPurchase,
  setTicketsPurchase,
  setStep,
  canBuy,
  canShare = true,
}: {
  event: CreatedEvent;
  tickets: CreatedTicket[];
  ticketsPurchase: TicketPurchase[];
  setTicketsPurchase: (data: TicketPurchase[]) => void;
  canBuy: boolean;
  canShare?: boolean;
  setStep: (data: 1 | 2) => void;
}) => {
  const [sheetOpen, setSheetOpen] = useState<boolean>(false);
  return (
    <div className="flex w-full animate-fade-in flex-col gap-3 md:flex-row md:flex-wrap md:items-start md:justify-center md:gap-10">
      <div className="flex w-full flex-col gap-3 md:max-w-[500px]">
        <BgWrapper>
          <EventImage dimension={472} image={event.image} />
        </BgWrapper>

        <PreviewOrganizerDetails
          event={event}
          className="hidden md:flex"
          canShare={canShare}
        />
      </div>

      <div className="flex w-full flex-col gap-3 text-sm text-grey-950 md:max-w-[530px]">
        <BgWrapper className="flex flex-col gap-3">
          <p className="font-fraunces text-[32px] leading-[100%] tracking-[-0.04em] text-primary">
            {event?.name ?? ""}
          </p>

          <div className="text-sm text-grey-800">
            <RenderMarkdown>{event?.description ?? ""}</RenderMarkdown>
          </div>
        </BgWrapper>

        <BgWrapper>
          <p className="flex items-center gap-2 font-bold">
            <Icon
              svg="map-pin"
              width={20}
              height={20}
              className="text-primary"
            />
            {event?.location ?? ""}
          </p>
        </BgWrapper>

        <BgWrapper>
          <p className="flex items-center gap-2 font-bold">
            <Icon
              svg="calendar"
              width={20}
              height={20}
              className="text-primary"
            />
            {formatDate(event?.startDate, "ddd, MMMM DD, YYYY")}{" "}
            {formatDate(event?.startDate) === formatDate(event?.endDate)
              ? ""
              : `- ${formatDate(event?.endDate, "ddd, MMMM DD, YYYY")}`}
          </p>
        </BgWrapper>

        <BgWrapper>
          <p className="flex items-center gap-2 font-bold">
            <Icon svg="clock" width={20} height={20} className="text-primary" />
            {event?.startTime ?? ""} - {event?.endTime ?? ""}
          </p>
        </BgWrapper>

        <PreviewOrganizerDetails
          event={event}
          className="mb-20 flex md:hidden"
          canShare={canShare}
        />

        <div
          className={classNames("md:!hidden", "transition-all duration-300", {
            "fixed inset-0 z-[998] bg-black/50": sheetOpen,
            "h-0": !sheetOpen,
          })}
          onClick={() => {
            setSheetOpen(false);
          }}
        />

        <BgWrapper
          className={classNames(
            "flex flex-col gap-4",
            "rounded-b-none md:rounded-b-2xl",
            "md:relative",
            "fixed bottom-0 left-0 right-0",
            "z-[999]",
          )}
        >
          <MiniHeader
            header="Choose Ticket"
            className={classNames("md:!flex", "transition-all duration-300", {
              flex: sheetOpen,
              hidden: !sheetOpen,
            })}
          />

          <div
            className={classNames(
              "overflow-hidden md:!h-fit",
              "transition-all duration-300",
              {
                "h-[253px] overflow-y-scroll": sheetOpen,
                "h-0": !sheetOpen,
              },
            )}
          >
            {(tickets?.length ?? 0) > 0 ? (
              <ChooseTickets
                tickets={tickets ?? []}
                ticketsPurchase={ticketsPurchase}
                setTicketsPurchase={setTicketsPurchase}
                showControls={true}
              />
            ) : (
              <EmptyState
                text="No Tickets Available"
                subtext="There aren't any tickets available for this event. Check back later."
              />
            )}
          </div>

          <Button
            text="Register"
            type="button"
            onClick={() => {
              setStep(2);
            }}
            disabled={ticketsPurchase.length === 0 || !canBuy}
            className="!hidden md:!flex"
          />

          <Button
            text={sheetOpen ? "Continue" : "Get a ticket"}
            type="button"
            onClick={() => {
              if (sheetOpen) {
                setStep(2);
              } else {
                setSheetOpen(true);
              }
            }}
            disabled={
              (sheetOpen && ticketsPurchase.length === 0) ||
              (sheetOpen && !canBuy)
            }
            className="md:!hidden"
          />
        </BgWrapper>
      </div>
    </div>
  );
};
